import { useCallback, useMemo, useState } from 'preact/hooks';
import {
  Editor,
  ErrorTile,
  Grid,
  HudContext,
  SyncIndicator,
  TabBar,
  type HudContextValue,
} from '@hud/ui';
import { dashboardPath } from '@hud/core';
import { createVault } from './vault.js';
import {
  clearConnection,
  defaultConnection,
  loadConnection,
  saveConnection,
  type Connection,
} from './connection.js';
import { useDashboard } from './useDashboard.js';
import { useRoute } from './useRoute.js';
import { Setup } from './Setup.js';

export function App() {
  const [connection, setConnection] = useState<Connection | null>(
    () => loadConnection() ?? defaultConnection(),
  );
  const [editingConnection, setEditingConnection] = useState(false);

  const connect = useCallback((next: Connection) => {
    saveConnection(next);
    setConnection(next);
    setEditingConnection(false);
  }, []);

  if (!connection || editingConnection) {
    return (
      <Setup
        initial={connection}
        onConnect={connect}
        {...(editingConnection ? { onCancel: () => setEditingConnection(false) } : {})}
      />
    );
  }

  return (
    <Dashboard
      // Remounting on connection change is intentional: the store, its pending
      // queue, and every cached blob belong to one vault and must not survive
      // a switch to another.
      key={JSON.stringify(connection)}
      connection={connection}
      onReconfigure={() => setEditingConnection(true)}
      onDisconnect={() => {
        clearConnection();
        setConnection(defaultConnection());
      }}
    />
  );
}

interface DashboardProps {
  connection: Connection;
  onReconfigure: () => void;
  onDisconnect: () => void;
}

function Dashboard({ connection, onReconfigure, onDisconnect }: DashboardProps) {
  const name = useRoute();
  const binding = useMemo(() => createVault(connection), [connection]);
  const { state } = useDashboard(binding.fs, name);
  const [editing, setEditing] = useState<string | null>(null);

  const context = useMemo<HudContextValue | null>(
    () =>
      state.status === 'ready'
        ? {
            store: state.store,
            config: state.config,
            assetUrl: binding.assetUrl,
            ...(binding.assetDataUrl ? { assetDataUrl: binding.assetDataUrl } : {}),
            openEditor: setEditing,
          }
        : null,
    [state, binding],
  );

  if (state.status === 'loading') {
    return <div class="hud-root" />;
  }

  if (state.status === 'failed') {
    return (
      <div class="hud-root">
        <ErrorTile title={`Cannot reach ${binding.label}`} message={state.message} />
        <div class="hud-recovery">
          <button type="button" onClick={onReconfigure}>
            Change connection
          </button>
          <button type="button" onClick={onDisconnect}>
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  const { loaded } = state;

  // A missing or invalid dashboard file is the one error worth taking over the
  // whole screen — there's no layout left to render around it.
  if (loaded.error) {
    return (
      <div class="hud-root">
        <ErrorTile title="Dashboard error" path={loaded.path} message={loaded.error} />
        <div class="hud-recovery">
          <button type="button" onClick={onReconfigure}>
            Change connection
          </button>
        </div>
      </div>
    );
  }

  const { tabs } = state.config;
  const hasTabs = tabs.length > 0;

  // Without this there's no way to switch vaults short of clearing site data.
  // It rides in the tab bar when there is one, since both want the same corner.
  const settings = (
    <button
      class={hasTabs ? 'hud-settings-button hud-settings-button--inline' : 'hud-settings-button'}
      type="button"
      title={`Connected to ${binding.label}`}
      aria-label="Connection settings"
      onClick={onReconfigure}
    >
      ⚙
    </button>
  );

  return (
    <HudContext.Provider value={context}>
      <div class={hasTabs ? 'hud-root hud-root--tabs' : 'hud-root'}>
        {loaded.widgets.length === 0 ? (
          <div class="hud-empty">
            <div class="hud-empty__title">No widgets yet</div>
            <div class="hud-empty__hint">add a layout entry to {dashboardPath(name)}</div>
          </div>
        ) : (
          <Grid grid={loaded.dashboard.grid} widgets={loaded.widgets} />
        )}
      </div>
      <Editor path={editing} onClose={() => setEditing(null)} />
      <SyncIndicator />
      {hasTabs ? <TabBar tabs={tabs} active={name} trailing={settings} /> : settings}
    </HudContext.Provider>
  );
}
